/*
 * Sonatype Nexus (TM) Open Source Version
 * Copyright (c) 2008-present Sonatype, Inc.
 * All rights reserved. Includes the third-party code listed at http://links.sonatype.com/products/nexus/oss/attributions.
 *
 * This program and the accompanying materials are made available under the terms of the Eclipse Public License Version 1.0,
 * which accompanies this distribution and is available at http://www.eclipse.org/legal/epl-v10.html.
 *
 * Sonatype Nexus (TM) Professional Version is available from Sonatype, Inc. "Sonatype" and "Sonatype Nexus" are trademarks
 * of Sonatype, Inc. Apache Maven is a trademark of the Apache Software Foundation. M2eclipse is a trademark of the
 * Eclipse Foundation. All other trademarks are the property of their respective owners.
 */

import React from 'react';
import { Box, Flex, Grid, Text, TextField, VisuallyHidden } from '@radix-ui/themes';
import { Search, SearchX, ShieldCheck } from 'lucide-react';

import { EmptyState } from '../../../../shared/EmptyState';
import { ErrorState } from '../../../../shared/ErrorState';
import { FilterSidebar, type FilterSection } from '../../../../shared/FilterSidebar';
import { LoadingState } from '../../../../shared/LoadingState';
import { TablePagination } from '../../../../shared/TablePagination/TablePagination';
import { EvaluationsTable } from './EvaluationsTable';
import { EVALUATIONS_STRINGS as STRINGS } from './evaluations.strings';
import { FILTER_IDS, MAX_SEARCH_LENGTH, PAGE_SIZES, THREAT_LEVELS, type EvaluationsQuery } from './evaluations.types';
import { useEvaluationsList } from './useEvaluationsList';

function buildFilterSections(formats: string[], query: EvaluationsQuery): FilterSection[] {
  const formatValues = [...new Set([...formats, ...query.formats])].sort();
  return [
    {
      id: FILTER_IDS.format,
      label: STRINGS.filters.format,
      type: 'checkbox',
      value: query.formats,
      options: formatValues.map((value) => ({ value, label: value })),
    },
    {
      id: FILTER_IDS.threatLevel,
      label: STRINGS.filters.threatLevel,
      type: 'checkbox',
      value: query.threatLevels,
      options: THREAT_LEVELS.map((value) => ({ value, label: STRINGS.threatLevels[value] })),
    },
    {
      id: FILTER_IDS.violations,
      label: STRINGS.filters.violations,
      type: 'checkbox',
      value: query.violations ? [query.violations] : [],
      options: [
        { value: 'with', label: STRINGS.filters.withViolations },
        { value: 'without', label: STRINGS.filters.withoutViolations },
      ],
    },
  ];
}

export function EvaluationsListView(): JSX.Element {
  const list = useEvaluationsList();
  const { query, rows, total, filtered, loading, failed, error, hasData, firstLoad, reloading } = list;

  const renderResults = () => {
    if (firstLoad) {
      return <LoadingState message={STRINGS.list.loading} />;
    }
    if (failed) {
      return (
        <ErrorState
          title={STRINGS.list.loadErrorTitle}
          message={error ?? ''}
          onRetry={list.retry}
          retryText={STRINGS.list.retry}
          retryAnalyticsId="nxrm-security-evaluations-retry-btn"
        />
      );
    }
    if (total === 0) {
      return <EmptyState icon={ShieldCheck} title={STRINGS.list.emptyTitle} description={STRINGS.list.emptyDescription} />;
    }
    if (rows.length === 0 && filtered > 0) {
      return (
        <EmptyState
          icon={SearchX}
          title={STRINGS.list.pageEmptyTitle}
          description={STRINGS.list.pageEmptyDescription}
          action={{
            label: STRINGS.list.firstPage,
            onClick: () => list.setPage(1),
            analyticsId: 'nxrm-security-evaluations-first-page-btn',
          }}
        />
      );
    }
    if (rows.length === 0) {
      return (
        <EmptyState
          icon={SearchX}
          title={STRINGS.list.noMatchesTitle}
          description={STRINGS.list.noMatchesDescription}
          action={{
            label: STRINGS.list.clearFilters,
            onClick: list.clearFilters,
            analyticsId: 'nxrm-security-evaluations-clear-filters-btn',
          }}
        />
      );
    }
    return <EvaluationsTable rows={rows} sortBy={query.sortBy} asc={query.asc} onSort={list.setSort} />;
  };

  const header = list.hasActiveFilters
    ? STRINGS.repositoriesFiltered(filtered, total)
    : STRINGS.repositoriesCount(total);
  const announcement = !hasData || loading || failed ? '' : filtered === 0 ? STRINGS.list.noResults : STRINGS.results(filtered);

  return (
    <Flex direction="column" gap="4">
      <VisuallyHidden>
        <div role="status" aria-live="polite">
          {announcement}
        </div>
      </VisuallyHidden>
      <Flex align="center" justify="between" gap="4" wrap="wrap">
        <Text data-testid="evaluations-header">{list.showHeader ? header : ''}</Text>
        <TextField.Root
          aria-label={STRINGS.list.searchLabel}
          placeholder={STRINGS.list.searchPlaceholder}
          data-analytics-id="nxrm-security-evaluations-search-input"
          maxLength={MAX_SEARCH_LENGTH}
          value={list.searchInput}
          onChange={(event) => list.setSearchInput(event.target.value)}
        >
          <TextField.Slot>
            <Search size={16} aria-hidden="true" />
          </TextField.Slot>
        </TextField.Root>
      </Flex>

      <Grid columns={{ initial: '1', sm: '250px 1fr' }} gap="4">
        <Box role="complementary" aria-label={STRINGS.list.filtersLabel}>
          <FilterSidebar
            sections={buildFilterSections(list.formats, query)}
            onFilterChange={list.changeFilter}
            onClear={list.clearFilters}
            disabled={firstLoad}
          />
        </Box>
        <Box
          role="region"
          aria-label={STRINGS.list.resultsLabel}
          aria-busy={reloading}
          style={reloading ? { opacity: 0.6, transition: 'opacity 0.15s' } : undefined}
        >
          <Flex direction="column" gap="4">
            {renderResults()}
            {filtered > 0 && !failed && (
              <TablePagination
                currentPage={query.page}
                totalPages={list.totalPages}
                itemsPerPage={query.pageSize}
                totalItems={filtered}
                pageSizeOptions={PAGE_SIZES}
                onPageChange={list.setPage}
                onItemsPerPageChange={list.setPageSize}
                mt="0"
              />
            )}
          </Flex>
        </Box>
      </Grid>
    </Flex>
  );
}
